import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Collapse,
} from "@mui/material";
import {
  Dashboard,
  ShoppingBag,
  RestaurantMenu,
  Repeat,
  LocalShipping,
  Group,
  Inventory,
  PersonPin,
  Business,
  Map,
  People,
  Science,
  WarningAmber,
  ExpandLess,
  ExpandMore,
  Assessment,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { useAuthStore } from "@/store/authStore";

interface NavLink {
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  children: NavLink[];
}

const navGroups: (NavLink | NavGroup)[] = [
  { label: "Tableau de bord", icon: <Dashboard />, path: "/dashboard" },
  { label: "Commandes", icon: <ShoppingBag />, path: "/orders" },
  { label: "Livraisons", icon: <LocalShipping />, path: "/deliveries/by-driver" },
  {
    label: "Menus",
    icon: <RestaurantMenu />,
    children: [
      { label: "Gestion des menus", icon: <RestaurantMenu />, path: "/menus" },
      { label: "Produits récurrents", icon: <Repeat />, path: "/recurrent-products" },
      { label: "Estimations", icon: <Assessment />, path: "/estimations" },
    ],
  },
  {
    label: "Catalogue",
    icon: <Inventory />,
    children: [
      { label: "Produits", icon: <Inventory />, path: "/products" },
      { label: "Ingrédients", icon: <Science />, path: "/ingredients" },
      { label: "Allergènes", icon: <WarningAmber />, path: "/allergens" },
    ],
  },
  {
    label: "Référentiel",
    icon: <Group />,
    children: [
      { label: "Clients", icon: <Group />, path: "/customers" },
      { label: "Entreprises", icon: <Business />, path: "/enterprises" },
      { label: "Utilisateurs", icon: <People />, path: "/users" },
      { label: "Livreurs", icon: <PersonPin />, path: "/drivers" },
      { label: "Catégories", icon: <CategoryIcon />, path: "/categories" },
      { label: "Zones", icon: <Map />, path: "/zones" },
    ],
  },
];

/** Menu réduit pour le rôle livreur (usage smartphone) */
const driverNavLinks: NavLink[] = [
  { label: "Estimations", icon: <Assessment />, path: "/estimations" },
  { label: "Livraisons", icon: <LocalShipping />, path: "/deliveries/my" },
];

function isGroup(item: NavLink | NavGroup): item is NavGroup {
  return "children" in item && Array.isArray((item as NavGroup).children);
}

function pathInGroup(pathname: string, group: NavGroup): boolean {
  return group.children.some(
    (c) =>
      pathname === c.path ||
      pathname.startsWith(`${c.path}/`) ||
      (c.path === "/deliveries/by-driver" && pathname.startsWith("/deliveries"))
  );
}

const linkStyle = {
  borderRadius: 2,
  mb: 0.5,
  "&.Mui-selected": {
    bgcolor: (theme: { palette: { primary: { main: string } } }) =>
      `${theme.palette.primary.main}1A`,
    color: "primary.main",
    "&:hover": {
      bgcolor: (theme: { palette: { primary: { main: string } } }) =>
        `${theme.palette.primary.main}26`,
    },
  },
};

/**
 * Sidebar de navigation principale
 * Basé sur la maquette défi_gourmand_admin_dashboard_1
 * Routes regroupées en sections repliables pour gagner de la place.
 */
export function Sidebar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((s) => s.user);
  const isDriver = user?.role === "DRIVER";

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      navGroups.forEach((item, idx) => {
        if (isGroup(item)) {
          initial[`group-${idx}`] = pathInGroup(pathname, item);
        }
      });
      return initial;
    }
  );

  useEffect(() => {
    const next: Record<string, boolean> = {};
    navGroups.forEach((item, idx) => {
      if (isGroup(item)) {
        next[`group-${idx}`] = pathInGroup(pathname, item);
      }
    });
    setOpenGroups((prev) => ({ ...prev, ...next }));
  }, [pathname]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isLinkActive = (path: string) =>
    pathname === path ||
    pathname.startsWith(`${path}/`) ||
    (path === "/deliveries/by-driver" && pathname.startsWith("/deliveries"));

  return (
    <Box
      sx={{
        width: 256,
        height: "100%",
        flexShrink: 0,
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 2,
        transition: "all 200ms ease",
      }}
    >
      {/* Brand / User Profile */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1,
            mb: 4,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "primary.main",
            }}
          >
            <RestaurantMenu />
          </Avatar>
          <Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, fontSize: "16px" }}
            >
              Défi Gourmand
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "primary.main",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {isDriver ? "Livreur" : "Admin"}
            </Typography>
          </Box>
        </Box>

        {/* Navigation Links */}
        <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {isDriver
            ? driverNavLinks.map((item) => {
                const isActive = isLinkActive(item.path);
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={isActive}
                      sx={linkStyle}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? "primary.main" : "text.secondary",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            : navGroups.map((item, idx) => {
            if (!isGroup(item)) {
              const isActive = isLinkActive(item.path);
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    selected={isActive}
                    sx={linkStyle}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? "primary.main" : "text.secondary",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }

            const groupKey = `group-${idx}`;
            const isOpen = openGroups[groupKey] ?? pathInGroup(pathname, item);

            return (
              <Box key={groupKey}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleGroup(groupKey)}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 40, color: "text.secondary" }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    />
                    {isOpen ? (
                      <ExpandLess sx={{ color: "text.secondary" }} />
                    ) : (
                      <ExpandMore sx={{ color: "text.secondary" }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {item.children.map((child) => {
                      const isActive = isLinkActive(child.path);
                      return (
                        <ListItem key={child.path} disablePadding>
                          <ListItemButton
                            onClick={() => navigate(child.path)}
                            selected={isActive}
                            sx={linkStyle}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive
                                  ? "primary.main"
                                  : "text.secondary",
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: "13px",
                                fontWeight: isActive ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

    </Box>
  );
}
